import { DomainError } from "../errors/index.js";
export class DiscoveryProbeService {
    #dialer;
    #onChange;
    #refresh;
    #rows = new Map();
    #generation = 0;
    constructor(options) {
        this.#dialer = options.dialer;
        this.#onChange = options.onChange ?? (() => undefined);
        this.#refresh = options.refresh;
    }
    snapshot() {
        return [...this.#rows.values()];
    }
    /** Probes every address in parallel, replacing the previous candidate set. */
    async probe(addresses) {
        const generation = (this.#generation += 1);
        const unique = dedupeText(addresses);
        this.#rows = new Map(unique.map((address) => [address, { address, status: "probing" }]));
        this.#emit();
        await Promise.all(unique.map((address) => this.#probeOne(generation, address)));
        return this.snapshot();
    }
    /**
     * Promotes a previously verified candidate as the device's home-node route. Fail-closed:
     * throws (never silently accepts) unless `probe()` most recently reported this exact
     * address as `"verified"` — "continue anyway" on an unverified or stale candidate is not
     * offered.
     */
    async promote(address) {
        const row = this.#rows.get(address);
        if (row === undefined || row.status !== "verified") {
            throw new DomainError({
                code: "invalid_request",
                message: "Only a verified discovery candidate may be promoted as the home-node route",
            });
        }
        const client = await this.#dial(address);
        await this.#refresh.refresh(client);
    }
    async #probeOne(generation, rawAddress) {
        let client;
        try {
            client = await this.#dial(rawAddress);
        }
        catch {
            this.#setRow(generation, rawAddress, { address: rawAddress, status: "unreachable" });
            return;
        }
        this.#setRow(generation, rawAddress, { address: rawAddress, status: "reachable" });
        try {
            const metadata = await client.invoke({
                operation: "GET /api/v2/node/metadata",
                payload: {},
            });
            const nodeId = textOrUndefined(metadata.node_id);
            if (nodeId === undefined) {
                // Reachable, but it never proved a stable node identity — fail-closed, never
                // "verified" on reachability alone.
                this.#setRow(generation, rawAddress, { address: rawAddress, status: "unreachable" });
                return;
            }
            const displayName = textOrUndefined(metadata.friendly_name) ?? "Home Node";
            this.#setRow(generation, rawAddress, {
                address: rawAddress,
                node: { displayName, maskedNodeId: maskNodeId(nodeId) },
                status: "verified",
            });
        }
        catch {
            this.#setRow(generation, rawAddress, { address: rawAddress, status: "unreachable" });
        }
    }
    async #dial(rawAddress) {
        const origin = secureLanOrigin(rawAddress);
        if (origin === undefined) {
            throw new DomainError({ code: "invalid_request", message: "Discovery candidate address is invalid" });
        }
        const candidate = { address: origin, id: rawAddress, path: "lan-direct" };
        const controller = new AbortController();
        return await this.#dialer.dial(candidate, controller.signal);
    }
    #setRow(generation, address, row) {
        if (generation !== this.#generation)
            return; // superseded by a later probe() call
        if (!this.#rows.has(address))
            return;
        this.#rows.set(address, row);
        this.#emit();
    }
    #emit() {
        this.#onChange(this.snapshot());
    }
}
function dedupeText(values) {
    return [...new Set(values.map((value) => value.trim()).filter((value) => value.length > 0))];
}
function textOrUndefined(value) {
    return typeof value === "string" && value.trim().length > 0 ? value.trim() : undefined;
}
function maskNodeId(nodeId) {
    if (nodeId.length <= 10)
        return nodeId;
    return `${nodeId.slice(0, 6)}····${nodeId.slice(-4)}`;
}
const LAN_ADDRESS_PATTERN = /^(?:https?:\/\/)?([^/?#:\s]+)(?::(\d{1,5}))?\/?$/u;
/**
 * Normalizes a user-typed (or metadata-sourced) LAN address into a strict HTTPS origin —
 * the shape `probeLanCandidate`/`secureOrigin` (`@econ-v1/adapters-web`) require — or
 * `undefined` if it is not a well-formed host[:port]. Mirrors
 * `peer-directory-service.ts`'s `toSecureAddress`.
 */
export function secureLanOrigin(address) {
    const trimmed = address.trim();
    if (trimmed.length === 0)
        return undefined;
    const match = LAN_ADDRESS_PATTERN.exec(trimmed);
    if (match === null)
        return undefined;
    const host = match[1];
    if (host === undefined || host.length === 0)
        return undefined;
    const port = match[2] === undefined ? undefined : Number(match[2]);
    if (port !== undefined && (port < 1 || port > 65535))
        return undefined;
    return port === undefined ? `https://${host}` : `https://${host}:${port}`;
}
//# sourceMappingURL=discovery-probe-service.js.map